import React, { useState, useCallback } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';
import {
  formatTIRADSCategory,
  getTIRADSRecommendation,
  hasTIRADSFeatures,
  NODULE_COMPOSITION,
  NODULE_ECHOGENICITY,
  NODULE_MARGINS,
  NODULE_SHAPE,
  ECHOGENIC_FOCI
} from '../generators/utils/pdfHelpers';

const ECHOTEXTURE_OPTIONS = [
  'Normal',
  'Heterogeneous',
  'Diffusely heterogeneous',
  'Hypoechoic',
  'Hyperechoic',
  'Not well visualized'
];

// "Where within the lobe" — not applicable to Isthmus nodules (no poles), so
// this list is only shown when a nodule's `lobe` is Right Lobe or Left Lobe.
const NODULE_LOCATION = [
  'Superior pole',
  'Mid pole',
  'Lower pole'
];

const NODULE_LOBE_OPTIONS = ['Right Lobe', 'Left Lobe', 'Isthmus'];

const VASCULARITY_OPTIONS = [
  'Normal',
  'Increased',
  'Decreased',
  'Avascular'
];

// Initial form data for Thyroid form
const INITIAL_THYROID_DATA = {
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: '',
  callReport: false,
  phoneNumber: '',
  history: '',
  comments: '',
  rightLobe: {
    length: '',
    width: '',
    depth: '',
    volume: '',
    echotexture: 'Normal',
    vascularity: 'Normal',
    hemithyroidectomy: false
  },
  leftLobe: {
    length: '',
    width: '',
    depth: '',
    volume: '',
    echotexture: 'Normal',
    vascularity: 'Normal',
    hemithyroidectomy: false
  },
  isthmus: {
    thickness: '',
    echotexture: 'Normal',
    surgicallyAbsent: false
  },
  // Unified nodule list — each nodule carries its own `lobe` ('Right Lobe' |
  // 'Left Lobe' | 'Isthmus') so it can be reassigned later, and gets its own
  // tab (keyed by `nodule-${id}`) instead of an ever-growing inline list.
  nodules: [],
  lymphNodes: '',
  thyroidectomy: false
};

function ThyroidForm({ token, onBack, facilityInfo }) {
  // Tab state: 'right' | 'left' | 'isthmus' | `nodule-${id}`
  const [activeTab, setActiveTab] = useState('right');
  // Fetch dynamic technologists list
  const { technologists, defaultTech } = useTechnologists();

  // Use the custom hook
  const {
    formData,
    updateField,
    updateNestedField,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    resetForm,
    isLoading,
    error,
    success,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
    {
      ...INITIAL_THYROID_DATA,
      technologist: defaultTech
    },
    'Thyroid',
    {
    token, facilityInfo
  });

  // Update technologist when defaultTech changes
  React.useEffect(() => {
    if (defaultTech && !formData.technologist) {
      updateField('technologist', defaultTech);
    }
  }, [defaultTech, formData.technologist, updateField]);

  // Calculate thyroid lobe volume: Length × Width × Depth × 0.52
  const calculateVolume = (length, width, depth) => {
    if (!length || !width || !depth) return '';
    const volume = parseFloat(length) * parseFloat(width) * parseFloat(depth) * 0.52;
    return volume.toFixed(1);
  };

  const handleLobeChange = useCallback((lobe, field, value) => {
    const newLobe = { ...formData[lobe], [field]: value };

    // Auto-calculate volume when dimensions change
    if (field === 'length' || field === 'width' || field === 'depth') {
      newLobe.volume = calculateVolume(newLobe.length, newLobe.width, newLobe.depth);
    }

    updateField(lobe, newLobe);
  }, [formData, updateField]);

  // Which main tab a nodule "belongs to" — used to return there after removal.
  const lobeTabKey = (lobe) => (lobe === 'Right Lobe' ? 'right' : lobe === 'Left Lobe' ? 'left' : 'isthmus');

  // Adds a nodule pre-set to the lobe whose "+ Add Nodule" button was
  // clicked, then immediately switches to its new tab.
  const addNodule = useCallback((lobe) => {
    const newId = formData.nodules.length > 0 ? Math.max(...formData.nodules.map(n => n.id)) + 1 : 1;
    const newNodule = {
      id: newId,
      lobe,
      length: '',
      width: '',
      depth: '',
      volume: '',
      location: lobe === 'Isthmus' ? '' : 'Mid pole',
      composition: '',
      echogenicity: '',
      margins: '',
      shape: '',
      echogenicFoci: '',
      description: '',
      tiradsScore: 0
    };
    updateField('nodules', [...formData.nodules, newNodule]);
    setActiveTab(`nodule-${newId}`);
  }, [formData, updateField]);

  // Calculate TI-RADS score for a nodule
  const calculateTIRADS = (nodule) => {
    let score = 0;
    const comp = NODULE_COMPOSITION.find(c => c.value === nodule.composition);
    if (comp) score += comp.points;
    const echo = NODULE_ECHOGENICITY.find(e => e.value === nodule.echogenicity);
    if (echo) score += echo.points;
    const marg = NODULE_MARGINS.find(m => m.value === nodule.margins);
    if (marg) score += marg.points;
    const shp = NODULE_SHAPE.find(s => s.value === nodule.shape);
    if (shp) score += shp.points;
    const foci = ECHOGENIC_FOCI.find(f => f.value === nodule.echogenicFoci);
    if (foci) score += foci.points;
    return score;
  };

  const getTIRADSDisplay = formatTIRADSCategory;

  const removeNodule = useCallback((id) => {
    const nodule = formData.nodules.find(n => n.id === id);
    const newNodules = formData.nodules.filter(n => n.id !== id);
    updateField('nodules', newNodules);
    if (activeTab === `nodule-${id}` && nodule) {
      setActiveTab(lobeTabKey(nodule.lobe));
    }
  }, [formData, updateField, activeTab]);

  const handleNoduleChange = useCallback((id, field, value) => {
    const updatedNodules = formData.nodules.map((nodule) => {
      if (nodule.id === id) {
        const updated = { ...nodule, [field]: value };
        if (field === 'length' || field === 'width' || field === 'depth') {
          updated.volume = calculateVolume(updated.length, updated.width, updated.depth);
        }
        if (field === 'lobe' && value === 'Isthmus') {
          updated.location = '';
        } else if (field === 'lobe' && !updated.location) {
          updated.location = 'Mid pole';
        }
        if (['composition', 'echogenicity', 'margins', 'shape', 'echogenicFoci'].includes(field)) {
          updated.tiradsScore = calculateTIRADS(updated);
        }
        return updated;
      }
      return nodule;
    });
    updateField('nodules', updatedNodules);
  }, [formData, updateField]);

  // Short tab-button label, e.g. "R1" / "L2" / "I1" — numbered per lobe, in
  // the order that lobe's nodules were added.
  const getNoduleTabLabel = (nodule) => {
    const sameLobe = formData.nodules.filter(n => n.lobe === nodule.lobe);
    const idx = sameLobe.findIndex(n => n.id === nodule.id);
    const prefix = nodule.lobe === 'Right Lobe' ? 'R' : nodule.lobe === 'Left Lobe' ? 'L' : 'I';
    return `${prefix}${idx + 1}`;
  };

  const renderNoduleFields = (nodule) => (
    <>
      <div className="form-group">
        <label>Lobe:</label>
        <select
          value={nodule.lobe}
          onChange={(e) => handleNoduleChange(nodule.id, 'lobe', e.target.value)}
        >
          {NODULE_LOBE_OPTIONS.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>

      {nodule.lobe !== 'Isthmus' && (
        <div className="form-group">
          <label>Location:</label>
          <select
            value={nodule.location}
            onChange={(e) => handleNoduleChange(nodule.id, 'location', e.target.value)}
          >
            {NODULE_LOCATION.map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
        </div>
      )}

      <div className="form-row" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ flex: '0 0 auto' }}>
          <label>Length (cm):</label>
          <input
            type="number"
            step="0.1"
            value={nodule.length}
            onChange={(e) => handleNoduleChange(nodule.id, 'length', e.target.value)}
            style={{ width: '80px' }}
          />
        </div>
        <div className="form-group" style={{ flex: '0 0 auto' }}>
          <label>Width (cm):</label>
          <input
            type="number"
            step="0.1"
            value={nodule.width}
            onChange={(e) => handleNoduleChange(nodule.id, 'width', e.target.value)}
            style={{ width: '80px' }}
          />
        </div>
        <div className="form-group" style={{ flex: '0 0 auto' }}>
          <label>Depth (cm):</label>
          <input
            type="number"
            step="0.1"
            value={nodule.depth}
            onChange={(e) => handleNoduleChange(nodule.id, 'depth', e.target.value)}
            style={{ width: '80px' }}
          />
        </div>
        <div className="form-group" style={{ flex: '0 0 auto' }}>
          <label>Volume (cc):</label>
          <input
            type="text"
            value={nodule.volume}
            readOnly
            style={{ backgroundColor: 'var(--color-surface)', width: '80px' }}
            placeholder="Auto"
          />
        </div>
      </div>

      <div className="form-row" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ flex: '1 1 30%', minWidth: '180px' }}>
          <label>Composition:</label>
          <select
            value={nodule.composition}
            onChange={(e) => handleNoduleChange(nodule.id, 'composition', e.target.value)}
          >
            <option value="">Select...</option>
            {NODULE_COMPOSITION.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ flex: '1 1 30%', minWidth: '180px' }}>
          <label>Echogenicity:</label>
          <select
            value={nodule.echogenicity}
            onChange={(e) => handleNoduleChange(nodule.id, 'echogenicity', e.target.value)}
          >
            <option value="">Select...</option>
            {NODULE_ECHOGENICITY.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ flex: '1 1 30%', minWidth: '180px' }}>
          <label>Margins:</label>
          <select
            value={nodule.margins}
            onChange={(e) => handleNoduleChange(nodule.id, 'margins', e.target.value)}
          >
            <option value="">Select...</option>
            {NODULE_MARGINS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="form-group" style={{ flex: '1 1 30%', minWidth: '180px' }}>
          <label>Shape:</label>
          <select
            value={nodule.shape}
            onChange={(e) => handleNoduleChange(nodule.id, 'shape', e.target.value)}
          >
            <option value="">Select...</option>
            {NODULE_SHAPE.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ flex: '1 1 30%', minWidth: '180px' }}>
          <label>Echogenic Foci:</label>
          <select
            value={nodule.echogenicFoci}
            onChange={(e) => handleNoduleChange(nodule.id, 'echogenicFoci', e.target.value)}
          >
            <option value="">Select...</option>
            {ECHOGENIC_FOCI.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ flex: '1 1 30%', minWidth: '180px' }}>
          <label>TI-RADS Score:</label>
          <input
            type="text"
            value={hasTIRADSFeatures(nodule) ? getTIRADSDisplay(nodule.tiradsScore || 0) : 'Select features'}
            readOnly
            style={{
              backgroundColor: 'var(--color-info-bg)',
              fontWeight: 'bold',
              border: '2px solid var(--color-info-border)',
              color: 'var(--color-info-text)'
            }}
          />
        </div>
        <div className="form-group" style={{ flex: '1 1 30%', minWidth: '180px' }}>
          <label>Recommendation:</label>
          <input
            type="text"
            value={hasTIRADSFeatures(nodule)
              ? (getTIRADSRecommendation(nodule.tiradsScore || 0, nodule) || 'Enter nodule size')
              : 'Select features'}
            readOnly
            style={{
              backgroundColor: 'var(--color-warning-bg)',
              fontWeight: 'bold',
              border: '2px solid var(--color-warning)',
              color: 'var(--color-warning-text)'
            }}
          />
        </div>
      </div>

      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
        <label>Description:</label>
        <input
          type="text"
          value={nodule.description}
          onChange={(e) => handleNoduleChange(nodule.id, 'description', e.target.value)}
          placeholder="Additional description"
        />
      </div>
    </>
  );

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={hookHandleSubmit}>
        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button
              type="button"
              className="btn-close"
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history (e.g., thyroid nodule, hyperthyroidism)"
            />
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                checked={formData.thyroidectomy}
                onChange={(e) => updateField('thyroidectomy', e.target.checked)}
              />
              <span>Status post thyroidectomy</span>
            </label>
          </div>
        </div>

        {!formData.thyroidectomy && (
          <>
            {/* TAB NAVIGATION */}
            <div className="form-section">
              <div className="worksheet-tabs">
                <button
                  type="button"
                  onClick={() => setActiveTab('right')}
                  className={`worksheet-tab-btn${activeTab === 'right' ? ' active' : ''}`}
                >
                  Right Lobe
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('left')}
                  className={`worksheet-tab-btn${activeTab === 'left' ? ' active' : ''}`}
                >
                  Left Lobe
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('isthmus')}
                  className={`worksheet-tab-btn${activeTab === 'isthmus' ? ' active' : ''}`}
                >
                  Isthmus
                </button>
                {formData.nodules.map(nodule => (
                  <button
                    key={nodule.id}
                    type="button"
                    onClick={() => setActiveTab(`nodule-${nodule.id}`)}
                    className={`worksheet-tab-btn${activeTab === `nodule-${nodule.id}` ? ' active' : ''}`}
                    title={`${nodule.lobe} Nodule`}
                  >
                    {getNoduleTabLabel(nodule)}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="worksheet-tab-content">
                {/* Right Lobe Tab */}
                {activeTab === 'right' && (
                  <div className="tab-content-grid">
                    <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Right Lobe</h3>

                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                          type="checkbox"
                          checked={formData.rightLobe.hemithyroidectomy}
                          onChange={(e) => handleLobeChange('rightLobe', 'hemithyroidectomy', e.target.checked)}
                        />
                        <span>Right Hemithyroidectomy</span>
                      </label>
                    </div>

                    <div style={{
                      gridColumn: '1 / -1',
                      opacity: formData.rightLobe.hemithyroidectomy ? 0.5 : 1,
                      pointerEvents: formData.rightLobe.hemithyroidectomy ? 'none' : 'auto'
                    }}>
                      <div className="form-row" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                        <div className="form-group" style={{ flex: '0 0 auto' }}>
                          <label>Length (cm):</label>
                          <input
                            type="number"
                            step="0.1"
                            value={formData.rightLobe.length}
                            onChange={(e) => handleLobeChange('rightLobe', 'length', e.target.value)}
                            placeholder="0.0"
                            style={{ width: '80px' }}
                            disabled={formData.rightLobe.hemithyroidectomy}
                          />
                        </div>
                        <div className="form-group" style={{ flex: '0 0 auto' }}>
                          <label>Width (cm):</label>
                          <input
                            type="number"
                            step="0.1"
                            value={formData.rightLobe.width}
                            onChange={(e) => handleLobeChange('rightLobe', 'width', e.target.value)}
                            placeholder="0.0"
                            style={{ width: '80px' }}
                            disabled={formData.rightLobe.hemithyroidectomy}
                          />
                        </div>
                        <div className="form-group" style={{ flex: '0 0 auto' }}>
                          <label>Depth (cm):</label>
                          <input
                            type="number"
                            step="0.1"
                            value={formData.rightLobe.depth}
                            onChange={(e) => handleLobeChange('rightLobe', 'depth', e.target.value)}
                            placeholder="0.0"
                            style={{ width: '80px' }}
                            disabled={formData.rightLobe.hemithyroidectomy}
                          />
                        </div>
                        <div className="form-group" style={{ flex: '0 0 auto' }}>
                          <label>Volume (cc):</label>
                          <input
                            type="text"
                            value={formData.rightLobe.volume}
                            readOnly
                            style={{ backgroundColor: 'var(--color-surface)', width: '80px' }}
                            placeholder="Auto"
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Echotexture:</label>
                          <select
                            value={formData.rightLobe.echotexture}
                            onChange={(e) => handleLobeChange('rightLobe', 'echotexture', e.target.value)}
                            disabled={formData.rightLobe.hemithyroidectomy}
                          >
                            {ECHOTEXTURE_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Vascularity:</label>
                          <select
                            value={formData.rightLobe.vascularity}
                            onChange={(e) => handleLobeChange('rightLobe', 'vascularity', e.target.value)}
                            disabled={formData.rightLobe.hemithyroidectomy}
                          >
                            {VASCULARITY_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => addNodule('Right Lobe')}
                        className="add-mass-btn"
                        disabled={formData.rightLobe.hemithyroidectomy}
                      >
                        + Add Nodule
                      </button>
                    </div>
                  </div>
                )}

                {/* Left Lobe Tab */}
                {activeTab === 'left' && (
                  <div className="tab-content-grid">
                    <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Left Lobe</h3>

                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                          type="checkbox"
                          checked={formData.leftLobe.hemithyroidectomy}
                          onChange={(e) => handleLobeChange('leftLobe', 'hemithyroidectomy', e.target.checked)}
                        />
                        <span>Left Hemithyroidectomy</span>
                      </label>
                    </div>

                    <div style={{
                      gridColumn: '1 / -1',
                      opacity: formData.leftLobe.hemithyroidectomy ? 0.5 : 1,
                      pointerEvents: formData.leftLobe.hemithyroidectomy ? 'none' : 'auto'
                    }}>
                      <div className="form-row" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                        <div className="form-group" style={{ flex: '0 0 auto' }}>
                          <label>Length (cm):</label>
                          <input
                            type="number"
                            step="0.1"
                            value={formData.leftLobe.length}
                            onChange={(e) => handleLobeChange('leftLobe', 'length', e.target.value)}
                            placeholder="0.0"
                            style={{ width: '80px' }}
                            disabled={formData.leftLobe.hemithyroidectomy}
                          />
                        </div>
                        <div className="form-group" style={{ flex: '0 0 auto' }}>
                          <label>Width (cm):</label>
                          <input
                            type="number"
                            step="0.1"
                            value={formData.leftLobe.width}
                            onChange={(e) => handleLobeChange('leftLobe', 'width', e.target.value)}
                            placeholder="0.0"
                            style={{ width: '80px' }}
                            disabled={formData.leftLobe.hemithyroidectomy}
                          />
                        </div>
                        <div className="form-group" style={{ flex: '0 0 auto' }}>
                          <label>Depth (cm):</label>
                          <input
                            type="number"
                            step="0.1"
                            value={formData.leftLobe.depth}
                            onChange={(e) => handleLobeChange('leftLobe', 'depth', e.target.value)}
                            placeholder="0.0"
                            style={{ width: '80px' }}
                            disabled={formData.leftLobe.hemithyroidectomy}
                          />
                        </div>
                        <div className="form-group" style={{ flex: '0 0 auto' }}>
                          <label>Volume (cc):</label>
                          <input
                            type="text"
                            value={formData.leftLobe.volume}
                            readOnly
                            style={{ backgroundColor: 'var(--color-surface)', width: '80px' }}
                            placeholder="Auto"
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Echotexture:</label>
                          <select
                            value={formData.leftLobe.echotexture}
                            onChange={(e) => handleLobeChange('leftLobe', 'echotexture', e.target.value)}
                            disabled={formData.leftLobe.hemithyroidectomy}
                          >
                            {ECHOTEXTURE_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Vascularity:</label>
                          <select
                            value={formData.leftLobe.vascularity}
                            onChange={(e) => handleLobeChange('leftLobe', 'vascularity', e.target.value)}
                            disabled={formData.leftLobe.hemithyroidectomy}
                          >
                            {VASCULARITY_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => addNodule('Left Lobe')}
                        className="add-mass-btn"
                        disabled={formData.leftLobe.hemithyroidectomy}
                      >
                        + Add Nodule
                      </button>
                    </div>
                  </div>
                )}

                {/* Isthmus Tab */}
                {activeTab === 'isthmus' && (
                  <div className="tab-content-grid">
                    <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Isthmus</h3>

                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                          type="checkbox"
                          checked={formData.isthmus.surgicallyAbsent}
                          onChange={(e) => updateNestedField('isthmus', 'surgicallyAbsent', e.target.checked)}
                        />
                        <span>Surgically Absent</span>
                      </label>
                    </div>

                    <div style={{
                      gridColumn: '1 / -1',
                      opacity: formData.isthmus.surgicallyAbsent ? 0.5 : 1,
                      pointerEvents: formData.isthmus.surgicallyAbsent ? 'none' : 'auto'
                    }}>
                      <div className="form-group">
                        <label>Thickness (mm):</label>
                        <input
                          type="number"
                          step="0.1"
                          value={formData.isthmus.thickness}
                          onChange={(e) => updateNestedField('isthmus', 'thickness', e.target.value)}
                          placeholder="0.0"
                          disabled={formData.isthmus.surgicallyAbsent}
                        />
                      </div>
                      <div className="form-group">
                        <label>Echotexture:</label>
                        <select
                          value={formData.isthmus.echotexture}
                          onChange={(e) => updateNestedField('isthmus', 'echotexture', e.target.value)}
                          disabled={formData.isthmus.surgicallyAbsent}
                        >
                          {ECHOTEXTURE_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => addNodule('Isthmus')}
                        className="add-mass-btn"
                        disabled={formData.isthmus.surgicallyAbsent}
                      >
                        + Add Nodule
                      </button>
                    </div>
                  </div>
                )}

                {/* Nodule Tabs */}
                {formData.nodules.map(nodule => activeTab === `nodule-${nodule.id}` && (
                  <div key={nodule.id} className="tab-content-grid">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gridColumn: '1 / -1' }}>
                      <h3 style={{ margin: 0, color: 'var(--color-primary)' }}>
                        {nodule.lobe} Nodule {getNoduleTabLabel(nodule).slice(1)}
                      </h3>
                      <button type="button" onClick={() => removeNodule(nodule.id)} className="remove-btn">
                        Remove
                      </button>
                    </div>
                    {renderNoduleFields(nodule)}
                  </div>
                ))}
              </div>
            </div>

            {/* Lymph Nodes */}
            <div className="form-section">
              <h3>Lymph Nodes</h3>
              <div className="form-group">
                <label>Findings:</label>
                <textarea
                  rows="2"
                  value={formData.lymphNodes}
                  onChange={(e) => updateField('lymphNodes', e.target.value)}
                  placeholder="Describe any abnormal cervical lymph nodes..."
                />
              </div>
            </div>
          </>
        )}

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments or impressions..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="submit-btn"
          >
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button
            type="button"
            onClick={resetForm}
            disabled={isLoading}
            className="reset-btn"
          >
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default ThyroidForm;
